import { service } from '@ember/service'
import Component from '@glimmer/component'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import { FlashBox } from 'teacher-ui-client/components/flash-queue/flash-box'
import { modifier } from 'ember-modifier'

interface ShowOnFlashSignature {
  Element: Element
  Args: {
    Positional: [flashes?: unknown[]]
  }
}

/**
 * Show only when there are flashes, to ensure the flash/popover is last thing added to top-layer - useful if there are
 * other things in the top-layer.
 * If popover is already showing, calling showPopover again will put it back to top of top-layer.
 */
const showOnFlash = modifier<ShowOnFlashSignature>(function (el, [flashes]) {
  if (el instanceof HTMLElement && flashes?.length) {
    el.showPopover?.()
  }
})

interface Signature {
  Element: Element
}

/**
 * This component creates the container to display messages within.
 * @see https://github.com/poteto/ember-cli-flash
 */
export class FlashQueue extends Component<Signature> {
  @service flashQueue!: FlashQueueService

  get flashObjects() {
    return this.flashQueue.queue
  }

  <template>
    <flash-queue
      {{! disable pointer events (click/hover) to allow mouse events _between_ flash messages to go through to page }}
      {{! unset inset to undo some default browser styles for popovers }}
      class="pointer-events-none inset-[unset] block space-y-4 overflow-visible bg-transparent p-0 print:hidden"
      {{! Implemented via HTML Popover API, to appear in the top-layer }}
      popover
      {{showOnFlash this.flashObjects}}
      ...attributes
    >
      {{#each this.flashObjects as |flashObject|}}
        <FlashBox @flash={{flashObject}} />
      {{/each}}
    </flash-queue>
  </template>
}

export default FlashQueue
